import { cx } from '@emotion/css';
import { useContentfulImages } from '@snapchat/mw-contentful-client';
import { Block, Picture, twoColumnCollapseWidth } from '@snapchat/snap-design-system-marketing';
import type { FC } from 'react';
import { useCallback, useMemo, useState } from 'react';

import type { ContentfulIdVariable } from '../../../../hooks/useContentfulQuery';
import { useContentfulQuery } from '../../../../hooks/useContentfulQuery';
import type { ContentfulIdProps } from '../../../../types/contentful';
import { parseMedia } from '../../../../utils/media';
import { renderRichTextMarkingsOnly } from '../../../../utils/renderText/renderRichText';
import { customRenderParagraph } from '../../helpers';
import { ArDownloadForm } from './ArDownloadForm';
import {
  backgroundImageContainerCss,
  backgroundImageCss,
  blockContainerCss,
  blockContainerSpecificVersionCss,
  blockWrapperCss,
  contentCss,
  contentHeightBlockCss,
  contentHeightSpecificVersionCss,
  contentSpecificVersionCss,
  logoCss,
} from './ArDownloadFormBlock.styles';
import type { ArDownloadFormBlockQuery } from './arDownloadFormBlockQuery';
import { query } from './arDownloadFormBlockQuery';
import { ArDownloadFormHeader } from './ArDownloadFormHeader';
import { ArDownloadSuccess } from './ArDownloadSuccess';
import { SupportedPlatforms } from './SupportedPlatforms';

type ArDownloadFormProps = ContentfulIdProps & {
  id: string;
};

/**
 * This component is not optimized for mobile, and only displays on desktop For mobile, the
 * ArArkoseProtectedFormBlock is used instead.
 */
export const ArDownloadFormBlock: FC<ArDownloadFormProps> = ({ id }) => {
  const [downloadUrl, setDownloadUrl] = useState<string | undefined>(undefined);
  const { getImageSources } = useContentfulImages();

  const { data } = useContentfulQuery<ArDownloadFormBlockQuery, ContentfulIdVariable>(query, {
    variables: { id },
  });

  const forSpecificVersion = !(data?.arDownloadFormBlock?.isLatestVersion ?? false);

  const onDownloadSuccess = useCallback((downloadUrl?: string) => {
    setDownloadUrl(downloadUrl);
  }, []);

  const { imageSource: backgroundImageSource } = parseMedia(
    data?.arDownloadFormBlock?.backgroundMedia
  );
  const { imageSource: backgroundImageSourceMobile } = parseMedia(
    data?.arDownloadFormBlock?.backgroundMediaMobile
  );

  const backgroundImageSources = useMemo(() => {
    const desktop = getImageSources(backgroundImageSource);
    if (!backgroundImageSourceMobile) return desktop;

    const mobile = getImageSources(backgroundImageSourceMobile);
    const mobileSources =
      mobile?.sources.map(s => ({
        ...s,
        media: `(max-width: ${twoColumnCollapseWidth}px)`,
      })) || [];

    return {
      ...desktop,
      sources: [...mobileSources, ...(desktop?.sources || [])],
      default: desktop?.default || '',
    };
  }, [backgroundImageSource, backgroundImageSourceMobile, getImageSources]);

  if (!data?.arDownloadFormBlock) {
    return null;
  }

  const {
    backgroundColor,
    title: titleRichText,
    supertitle: supertitleRichText,
    subtitle: subtitleRichText,
    logo,
    endpoint,
    analytics,
    submitText,
    submitSuccessText,
    successBlock,
    version,
    platform,
    eula,
    supportedPlatforms,
    supportedPlatformsLabel: supportedPlatformsLabelRichText,
    releaseNotesLinkText: releaseNotesLinkTextRichText,
  } = data.arDownloadFormBlock;

  const title = renderRichTextMarkingsOnly(titleRichText);
  const supertitle = renderRichTextMarkingsOnly(supertitleRichText);
  const subtitle = renderRichTextMarkingsOnly(subtitleRichText);
  const supportedPlatformsLabel = customRenderParagraph(supportedPlatformsLabelRichText);
  const releaseNotesLinkText = customRenderParagraph(releaseNotesLinkTextRichText);

  const platforms = supportedPlatforms || [];

  const { imageSource: logoImageSource, imageAltText: logoAltText } = parseMedia(logo);

  const downloadSuccess = downloadUrl !== undefined && downloadUrl !== null;

  if (downloadSuccess) {
    return (
      <ArDownloadSuccess
        successBlock={successBlock}
        downloadUrl={downloadUrl}
        logoImageSource={logoImageSource}
        logoAltText={logoAltText}
      />
    );
  }

  return (
    <div className={cx('ar-download-from-container', blockWrapperCss)}>
      <Block
        backgroundColor={backgroundColor}
        className={cx(blockContainerCss, {
          [blockContainerSpecificVersionCss]: forSpecificVersion,
        })}
        preChildren={
          !forSpecificVersion ? (
            <>
              {backgroundImageSources && (
                <Picture
                  imgSrcs={backgroundImageSources}
                  className={backgroundImageContainerCss}
                  imgClassName={backgroundImageCss}
                  altText=""
                />
              )}
              <img className={logoCss} src={logoImageSource} alt={logoAltText} />
            </>
          ) : undefined
        }
      >
        <div
          className={cx('ar-download-form-content', contentCss, {
            [contentHeightBlockCss]: !forSpecificVersion,
            [contentSpecificVersionCss]: forSpecificVersion,
            [contentHeightSpecificVersionCss]: forSpecificVersion,
          })}
        >
          <ArDownloadFormHeader
            title={title}
            supertitle={supertitle}
            version={version.initialValue}
            subtitle={subtitle}
            releaseNotesLinkText={releaseNotesLinkText}
            forSpecificVersion={forSpecificVersion}
          />
          <SupportedPlatforms
            platforms={platforms}
            supportedPlatformsLabel={supportedPlatformsLabel}
          />
          <ArDownloadForm
            id={id}
            analytics={analytics}
            endpoint={endpoint}
            version={version}
            eula={eula}
            platform={platform}
            submitText={submitText}
            submitSuccessText={submitSuccessText}
            useCompactText={forSpecificVersion}
            onDownloadSuccess={onDownloadSuccess}
          />
        </div>
      </Block>
    </div>
  );
};
